import { FC, useCallback, useMemo } from 'react'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { OnboardingPagePreset } from 'components/features/creditCard/creditCardOnboarding/OnboardingPagePreset/OnboardingPagePreset'
import styles from 'components/features/creditCard/creditCardOnboarding/PhoneStepVerifyForm/PhoneStepVerifyForm.styl'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import Button from 'components/features/pfm/Button/Button'
import { Link } from 'react-router-dom'
import { CreateCCPhoneAction, ResetCCPhoneVerificationAction, VerifyCCPhoneAction } from 'components/features/creditCard/creditCardStore/actions'
import { useNextStep } from 'components/features/creditCard/creditCardOnboarding/useNextStep'
import { useSMSRateLimit } from 'components/features/creditCard/creditCardOnboarding/useSMSRateLimit'
import { useStepNumber } from 'components/features/creditCard/creditCardOnboarding/useStepNumber'
import { LabeledInput } from 'components/atoms/inputs/LabeledInput/LabeledInput'
import { FormPreset } from 'components/atoms/inputs/FormPreset/FormPreset'
import { useForm } from 'react-hook-form'
import { getTimeLeftDisplay } from 'components/features/creditCard/creditCardOnboarding/helpers'
import { useRequestErrorsInForm } from 'hooks/useErrors'
import { verificationCodeSchema } from 'helpers/schemaHelpers'


export type VeritfyCodeForm = {
  verification_code: number
}

const schema = yup.object({
  verification_code: verificationCodeSchema,
})

const EMPTY_OBJ = {}
export const PhoneStepVerifyForm: FC = () => {

  const formContext = useForm<VeritfyCodeForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      verification_code: undefined,
    },
  })
  const { handleSubmit, setError, clearErrors, formState: { errors }} = formContext
  const verifyPhoneRequest = useStoreSelector((state) => state.app.creditCard.verifyPhoneRequest)
  const verifyErrorsMap = useStoreSelector((state) => state.app.creditCard.verifyPhoneRequest?.errorsMap ?? null)
  const phone_number = useStoreSelector((state) => state.app.creditCard.createPhoneRequest.data?.phone_number ?? null)
  const stepNo = useStepNumber('phone')
  const { isLoading } = verifyPhoneRequest

  useRequestErrorsInForm(verifyPhoneRequest, setError)

  const nextStep = useNextStep()
  const dispatch = useStoreDispatch()
  const handleResend = useCallback(() => {
    if (!phone_number) {
      return
    }

    dispatch(new CreateCCPhoneAction({
      person_data: {
        phone_number,
      },
      step: nextStep || 'ssn',
    }))

    return EMPTY_OBJ
  }, [dispatch, nextStep, phone_number])

  const onSubmit = useMemo(() => handleSubmit(({ verification_code }) => {
    if (phone_number === null) {
      return EMPTY_OBJ
    }

    dispatch(new VerifyCCPhoneAction({
      verify: {
        verification_code: verification_code.toString(),
        phone_number,
      },
      patch: {
        step: nextStep || 'ssn',
        person_data: {
          phone_number,
        },
      },
    }))
    clearErrors()
    return
  }), [clearErrors, dispatch, handleSubmit, nextStep, phone_number])

  const isTooManyAttempts = useMemo(() => {
    const { non_field_errors } = verifyErrorsMap || {}
    const [firstError] = non_field_errors || []

    return firstError === 'must_revalidate_phone_number'
  }, [verifyErrorsMap])

  const handleBack = useCallback(() => {
    dispatch(new ResetCCPhoneVerificationAction())
  }, [dispatch])

  const secondsLeft = useSMSRateLimit()

  const cardChild = useMemo(() => {
    let displayNumber = ''
    if (phone_number) {
      const parts = phone_number.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
      if (parts) {
        const [, part1, part2, part3] = parts
        displayNumber = `(${ part1 }) ${ part2 }-${ part3 }`
      }
    }

    return (
      <>
        { displayNumber && (
          <div className={ styles.note } data-qa-marker='form-header'>
            We sent a code to <strong>{ displayNumber }</strong>.
          </div>
        )}
        <div className={ styles.changeNumberLink }>
          <a onClick={ handleBack } data-qa-marker='change-number' data-qa-type='link'>Change Number</a>
        </div>
        <FormPreset formContext={ formContext } onSubmit={ onSubmit }>
          <LabeledInput
            name='verification_code'
            label='Verification Code'
            type='verification_code'
            data-qa-marker='code-input'
            dynamicHeight={ false }
            error={ errors.verification_code }
          />
          { displayNumber && !isTooManyAttempts && (
            <div className={ styles.resendLinkWrapper }>
              { !secondsLeft && (
                <a onClick={ handleResend } data-qa-marker='resend-code' data-qa-type='link'>Resend code</a>
              )}
              { !!secondsLeft && (
                <span data-qa-marker='resend-code-in' data-qa-type='link'>
                  Resend in { getTimeLeftDisplay(secondsLeft) }
                </span>
              )}
            </div>
          )}
        </FormPreset>
      </>
    )
  }, [errors.verification_code, formContext, handleBack, handleResend, isTooManyAttempts, onSubmit, phone_number, secondsLeft])

  return (
    <OnboardingPagePreset
      step={ stepNo }
      title='Enter the code we sent you'
      card={ cardChild }
      onBack={ handleBack }
      progressPercent={ 35 }
    >
      {
        isTooManyAttempts ? (
          <Button
            text='Back'
            onClick={ handleBack }
            fullWidth
            qaMarker='get-code'
            isSubmitting={ isLoading }
          />
        ) : (
          <Button
            text='Confirm Number'
            fullWidth
            qaMarker='confirm-number'
            isSubmitting={ isLoading }
            onClick={ onSubmit }
          />
        )
      }
      <Link to='/credit-card'>
        <Button
          text='Finish later'
          style='textOnly'
          fullWidth
          qaMarker='finish-later'
        />
      </Link>
    </OnboardingPagePreset>
  )
}
